import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Plus,
  Globe,
  Building2,
  ExternalLink,
  Search,
  Calendar as CalendarIcon,
  Users,
  Trash2,
  AlertTriangle,
  Pencil
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editForm, setEditForm] = useState({ package_type: '', industry: '' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    client_id: '',
    plan: 'managed',
    domain: '',
    industry: '',
    package_type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.allSettled([
        api.get('/projects'),
        api.get('/admin/users')
      ]);

      if (projectsRes.status === 'fulfilled') {
        setProjects(projectsRes.value.data || []);
      } else {
        console.error('Error fetching projects:', projectsRes.reason);
        toast.error('Failed to load projects');
      }

      if (usersRes.status === 'fulfilled') {
        const raw = usersRes.value.data;
        const allUsers = Array.isArray(raw) ? raw : (raw?.items || []);
        const clientUsers = allUsers.filter(u => u.role === 'client');
        setClients(clientUsers);
      } else {
        console.error('Error fetching users:', usersRes.reason);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.client_id || !form.domain) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await api.post('/projects', form);
      toast.success('Project created successfully');
      setDialogOpen(false);
      setForm({ name: '', client_id: '', plan: 'managed', domain: '', industry: '', package_type: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProject) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteProject.project_id}`);
      toast.success(`"${deleteProject.name}" deleted successfully`);
      setDeleteProject(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (project) => {
    setEditProject(project);
    setEditForm({
      package_type: project.package_type || '',
      industry: project.industry || '',
    });
  };

  const handleEdit = async () => {
    if (!editProject) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.package_type) payload.package_type = editForm.package_type;
      if (editForm.industry) payload.industry = editForm.industry;
      await api.patch(`/projects/${editProject.project_id}`, payload);
      toast.success('Project updated');
      setEditProject(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.user_id === clientId);
    return client?.name || client?.company_name || clientId;
  };

  const getPlanBadge = (plan) => {
    const plans = {
      diy: { label: 'DIY', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
      managed: { label: 'Managed', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
      corporate: { label: 'Corporate', className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' }
    };
    return plans[plan] || plans.managed;
  };

  const filtered = projects.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.domain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Projects</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage client projects and campaigns
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gds-btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Project Name *</Label>
                <Input
                  placeholder="e.g. Demo Corp SEO Campaign"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {c.name} ({c.company_name || c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Domain *</Label>
                <Input
                  placeholder="https://example.com"
                  value={form.domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diy">DIY</SelectItem>
                      <SelectItem value="managed">Managed</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Industry</Label>
                  <Input
                    placeholder="e.g. SaaS, E-commerce"
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Package Tier</Label>
                <Select value={form.package_type} onValueChange={(v) => setForm({ ...form, package_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a package tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="platinum">Platinum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full gds-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{projects.length}</p>
                <p className="text-sm text-slate-600">Total Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {projects.filter(p => p.status === 'active').length}
                </p>
                <p className="text-sm text-slate-600">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{clients.length}</p>
                <p className="text-sm text-slate-600">Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {projects.filter(p => p.plan === 'corporate').length}
                </p>
                <p className="text-sm text-slate-600">Corporate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="gds-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">All Projects</CardTitle>
              <CardDescription>{projects.length} project{projects.length !== 1 ? 's' : ''} total</CardDescription>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Target className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">
                {projects.length === 0 ? 'No projects yet' : 'No matching projects'}
              </p>
              <p className="text-sm mt-1">
                {projects.length === 0
                  ? 'Create your first project to get started'
                  : 'Try adjusting your search query'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((project) => {
                const planBadge = getPlanBadge(project.plan);
                return (
                  <motion.div
                    key={project.project_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Target className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{project.name}</h3>
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                            <Building2 className="w-3 h-3" />
                            {getClientName(project.client_id)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={planBadge.className}>{planBadge.label}</Badge>
                        {project.package_type && (
                          <Badge className={{
                            basic: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                            silver: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
                            gold: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                            platinum: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                          }[project.package_type] || 'bg-slate-100 text-slate-600'}>
                            {project.package_type.charAt(0).toUpperCase() + project.package_type.slice(1)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {project.domain && (
                      <a
                        href={project.domain.startsWith('http') ? project.domain : `https://${project.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-3"
                      >
                        <Globe className="w-4 h-4" />
                        {project.domain}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {project.created_at
                          ? new Date(project.created_at).toLocaleDateString()
                          : 'N/A'}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
                          {project.status || 'active'}
                        </Badge>
                        <button
                          onClick={() => openEdit(project)}
                          className="p-1 rounded text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                          title="Edit project"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteProject(project)}
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete project"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Project Dialog */}
      <Dialog open={!!editProject} onOpenChange={(open) => !open && setEditProject(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Project — {editProject?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Package Tier</Label>
              <Select value={editForm.package_type} onValueChange={(v) => setEditForm({ ...editForm, package_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a package tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Industry</Label>
              <Input
                placeholder="e.g. SaaS, E-commerce"
                value={editForm.industry}
                onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditProject(null)} disabled={saving}>Cancel</Button>
              <Button className="gds-btn-primary" onClick={handleEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteProject} onOpenChange={(open) => !open && setDeleteProject(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Project
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Are you sure you want to delete{' '}
              <strong className="text-slate-900 dark:text-slate-100">
                "{deleteProject?.name}"
              </strong>
              ? This action cannot be undone and will permanently remove all project data.
            </p>
            {deleteProject?.domain && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-slate-500">
                <Globe className="w-4 h-4 shrink-0" />
                {deleteProject.domain}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteProject(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <span className="animate-spin mr-2 w-3 h-3 border-2 border-white border-t-transparent rounded-full inline-block" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Project
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
